import { useRef, JSX } from 'react';
import './style.scss';
import { IdHelper } from '../../../../../helpers/id.helper';
import type { ITrafficSituation } from '../../../../../models/traffic-situation.model';
import type { ILine } from '../../../../../models/line.model';
import { TrafficSituationHelper } from '../../../../../helpers/traffic-situation.helper';
import { TranslationService } from '../../../../../services/translation.service';
import { AnalyticsService } from '../../../../../services/analytics-service';
import LineListShowcase from '../../../../Shared/LineListShowcase';
import TrafficSituationSeverityIcon from '../../../components/TrafficSituationSeverityIcon';
import EpiserverHelper from '../../../../../helpers/episerver.helper';
import DataUrlHelper from '../../../../../helpers/data-url.helper';
import calendarGray900 from '../../../../../../assets/icons/calendar-gray-900.svg';
import calendarPlannedGray900 from '../../../../../../assets/icons/calendar-planned-gray-900.svg';
import { TrafficInformationFeedbackService } from '../../../../../services/traffic-information-feedback.service';

// === EVENT HANDLERS ===
function handleShowMoreDetailsClick() {
  AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Klick på visa mer information', '');
}

function handleExpandTrafficSituationDetails(trafficSituation: ITrafficSituation) {
  AnalyticsService.addEvent(AnalyticsService.TrafficSituations, 'Visa störningsmeddelande-detaljer', '');
  TrafficInformationFeedbackService.registerExplicitFeedback(trafficSituation);
}

function TrafficSituationV2({ trafficSituation }: { trafficSituation: ITrafficSituation }) {
  // VARIABLES
  const distinctLines: ILine[] = TrafficSituationHelper.getDistinctLines(trafficSituation);
  const validityTimeText = TrafficSituationHelper.createValidityTimeText(trafficSituation, (x) => TranslationService.get(x));
  const trafficSituationRef = useRef<HTMLHeadingElement>(null);
  const id = IdHelper.newUniqueId('traffic-situation-collapsible-');
  const isNewTrafficSituation = TrafficSituationHelper.isNewTrafficSituation(trafficSituation);
  const isPlannedTrafficSituation = TrafficSituationHelper.isPlannedTrafficSituation(trafficSituation);

  function getTrafficSituationTagElement(text: string): JSX.Element {
    return <div className="traffic-situation-two-dot-zero__tag text-center d-flex align-items-center rounded">{text.toUpperCase()}</div>;
  }

  function getValidityTimeTextWithIcon(validityTimeText: string): JSX.Element {
    return (
      <div className="d-flex align-items-center">
        {isPlannedTrafficSituation ? (
          <>
            <img className="calendar-icon pe-2" src={DataUrlHelper.encode(calendarPlannedGray900)} alt=""></img>
            <div>
              <div>{TranslationService.get('trafficsituationspage.planned')}:&nbsp;</div>
              <div>{validityTimeText}</div>
            </div>
          </>
        ) : (
          <>
            <img className="traffic-situation-two-dot-zero__calendar-icon" src={DataUrlHelper.encode(calendarGray900)} alt=""></img>
            <div>{validityTimeText}</div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="traffic-situation-two-dot-zero p-2 p-sm-3 print-no-break border border-1 rounded-1 position-relative" ref={trafficSituationRef}>
      {EpiserverHelper.isInEditMode() && <span className="fw-bold">{trafficSituation.situationNumber}</span>}
      {isNewTrafficSituation && getTrafficSituationTagElement(TranslationService.get('trafficsituationspage.tag.new'))}
      <a
        className="d-flex traffic-situation-two-dot-zero__collapse-button collapsed text-decoration-none"
        data-bs-toggle="collapse"
        href={`#${id}`}
        role="button"
        aria-expanded="false"
        aria-controls={id}
        onClick={() => handleExpandTrafficSituationDetails(trafficSituation)}
      >
        <div className="d-flex flex-column flex-grow-1">
          <h3 className="d-flex align-items-center mb-2">
            <TrafficSituationSeverityIcon severity={trafficSituation.severity} className="traffic-situation-two-dot-zero__severity-icon"></TrafficSituationSeverityIcon>
            {!!trafficSituation.title && <span>{TrafficSituationHelper.getFirstSentenceInTitle(trafficSituation.title)}</span>}
          </h3>
          {distinctLines.length > 0 && <LineListShowcase lines={distinctLines} className="mb-2" />}
          {!!validityTimeText && getValidityTimeTextWithIcon(validityTimeText)}
        </div>
      </a>
      <div className="collapse" id={id}>
        {trafficSituation.description && <div className="mt-2">{trafficSituation.description}</div>}
        {trafficSituation.relativeDetailsUrl && (
          <div className="mt-1">
            <a href={trafficSituation.relativeDetailsUrl} onClick={handleShowMoreDetailsClick}>
              {TranslationService.get('trafficsituationspage.linktodetailspagetext')}
            </a>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrafficSituationV2;
